import * as d3 from "d3";
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  SimpleChange,
  ViewChild,
} from "@angular/core";
import { HttpService } from "../../shared/services/http.service";
import { ProjectService } from "../../shared/services/project.service";
import { ImageLoaderService } from "../../shared/services/image-loader.service";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { ImageFlipperService } from "../image-flipper.service";
import { AppConfig } from "../../app.config";

interface ShotImageRequestResponse {
  image: string;
  meta: {
    clip_value: [number, number];
  };
}
interface ShotRequestBody {
  iteration: number;
  shot_id: number;
  type: string;
  panel: number;
  start_trace: number;
  end_trace: number;
  scaling: [number, number];
  interleave_attrs: {
    is_interleave: boolean;
    extent: number;
  };
}
interface ImageInformation {
  job_name: string;
  iteration: number;
  shot_id?: number;
}
// The GetShotImages tool returns the shared AgentResponse shape (the same one
// GetSliceImages uses), normalised by responder._parse_structured before it
// reaches the browser. Every field is now an array:
//   image_links: string[]                 — one entry; we render the first
//   image_information: ImageInformation[]  — _parse_structured turns each JSON
//                                            string into an object
//   request_body: string[]                 — one entry, a JSON string (NOT
//                                            pre-parsed, unlike image_information)
// Older payloads (and some saved conversations) carried the singular shape
// (image_link / object image_information / string-or-object request_body), so
// accept both.
interface StructuredResponse {
  image_links?: string[];
  image_link?: string;
  image_information: ImageInformation | ImageInformation[];
  request_body:
    | ShotRequestBody
    | string
    | Array<ShotRequestBody | string>;
}

/**
 * Component for visualizing shot images within an SVG container.
 *
 * This component handles fetching, displaying, and updating images based on structured responses.
 * It supports dynamic resizing, panel type switching, and integration with an image flipper service.
 *
 * @remarks
 * - Uses D3.js for SVG manipulation and image rendering.
 * - Handles asynchronous image loading and error notification.
 * - Supports three panel types: "Interleaved", "Field", and "Predicted".
 * - Automatically updates image dimensions on window resize.
 *
 * @example
 * ```html
 * <app-shot-image-visualizer [messageContent]="response"></app-shot-image-visualizer>
 * ```
 *
 * @property {ElementRef} sliceImageContainer - Reference to the container element for the SVG image.
 * @property {StructuredResponse | null} messageContent - Input structured response containing image and metadata.
 * @property {ImageInformation} image_information - Metadata about the current image.
 * @property {string} plot_link - URL endpoint for fetching the image.
 * @property {ShotRequestBody} request_body - Request body used for image fetching.
 * @property {number} width - Width of the SVG/image.
 * @property {number} height - Height of the SVG/image.
 * @property {any} svg - D3 selection for the SVG element.
 * @property {string} image_object_url - Data URL for the loaded image.
 * @property {"Interleaved" | "Field" | "Predicted"} panel_type - Current panel type.
 * @property {number} scaling - Current scaling value for the image.
 * @property {object} margin - Margins for the SVG container.
 * @property {boolean} loading - Indicates if the image is currently loading.
 *
 * @method ngOnChanges - Handles changes to input properties and triggers image fetching.
 * @method ngAfterViewInit - Initializes the SVG container after view initialization.
 * @method get_image - Fetches the image from the backend and updates the display.
 * @method plot_image - Renders the image within the SVG container.
 * @method onScalingChange - Updates the scaling values for the image.
 * @method update_image_width - Updates the image width on container resize.
 * @method handlePanelTypeChange - Handles switching between panel types.
 * @method add_image_to_flipper - Adds the current image to the image flipper service.
 * @method sizeChange - Host listener for window resize events.
 */
@Component({
    selector: "app-shot-image-visualizer",
    templateUrl: "./shot-image-visualizer.component.html",
    styleUrls: ["./shot-image-visualizer.component.less"],
    standalone: false
})
export class ShotImageVisualizerComponent {
  @ViewChild("sliceImageContainer") sliceImageContainer: ElementRef;

  @Input() messageContent: null | StructuredResponse = null;
  // Emitted when the user applies parameter changes (the "Update" button) so the
  // parent can persist the new request_body back into the saved conversation.
  @Output() contentChange = new EventEmitter<ShotRequestBody>();
  image_information: ImageInformation;
  plot_link: string;
  request_body: ShotRequestBody;

  width: number = 500;
  height: number = 400;
  svg: any = null;
  image_object_url: string;
  panel_type: "Interleaved" | "Field" | "Predicted";
  scaling: number;

  margin: { left: number; right: number; top: number; bottom: number } = {
    left: 40,
    right: 0,
    top: 0,
    bottom: 40,
  };
  loading: boolean;
  // Controls the settings panel collapse, matching the slice visualizer. The
  // template binds it (shrinkHide / fold / unfold), so it must be declared here —
  // otherwise the Angular template type-check fails and the panel starts expanded.
  hide_settings: boolean = true;

  constructor(
    private httpService: HttpService,
    private projectService: ProjectService,
    private imageService: ImageLoaderService,
    private notificationService: NzNotificationService,
    private imageFlipper: ImageFlipperService
  ) {}

  async ngOnChanges(changes: SimpleChange) {
    try {
      const mc = this.messageContent;
      if (!mc) return;

      // image_information arrives as a one-element array (current shape) or a
      // bare object (legacy) — take the object either way.
      this.image_information = Array.isArray(mc.image_information)
        ? mc.image_information[0]
        : mc.image_information;

      // The link is image_links[0] (current) or image_link (legacy).
      let imageLink = Array.isArray(mc.image_links)
        ? mc.image_links[0]
        : mc.image_link;
      if (!imageLink) {
        throw new Error("No shot image link in the response");
      }
      // If the image link is a relative path (starts with /), prepend the API URL
      if (imageLink.startsWith("/")) {
        imageLink = `${AppConfig.settings.apiUrl}${imageLink}`;
      }
      this.plot_link = imageLink;

      // request_body now arrives as a one-element array (current shape), like
      // image_links / image_information; older payloads sent a bare value. Take
      // the first entry either way, then JSON.parse if it's still a string (the
      // backend normalises image_information but not request_body).
      const rb = Array.isArray(mc.request_body)
        ? mc.request_body[0]
        : mc.request_body;
      this.request_body = typeof rb === "string" ? JSON.parse(rb) : rb;

      this.image_information.shot_id = this.request_body.shot_id;
      await this.get_image();
    } catch (e) {
      console.log("error here in processing", e);
    }
  }

  ngAfterViewInit() {
    const containerElement = this.sliceImageContainer?.nativeElement;
    this.width = containerElement?.clientWidth;

    this.svg = d3
      .create("svg")
      .attr("width", this.width)
      .attr("height", this.height);
    // Append the SVG element to DOM.
    let chartElement = this.sliceImageContainer?.nativeElement as HTMLElement;
    if (chartElement) chartElement.append(this.svg.node());
  }

  /**
   * @param persist When true (the user clicked "Update"), the resolved
   * request_body is written back into messageContent and emitted so the parent
   * can save it to the conversation. Auto-fetches from ngOnChanges pass false.
   */
  async get_image(persist: boolean = false) {
    if (!this.plot_link) return;
    this.loading = true;
    try {
      await this.httpService
        .post(this.plot_link, this.request_body, {
          headers: this.projectService.getAuthorizationHeader(),
        })
        .toPromise()
        .then((res: ShotImageRequestResponse) => {
          this.image_object_url = `data:image/png;base64,${res.image}`;
          this.request_body.scaling = res.meta.clip_value;

          this.panel_type = this.request_body.interleave_attrs.is_interleave
            ? "Interleaved"
            : this.request_body.panel === 1
            ? "Field"
            : "Predicted";
          this.scaling = res.meta.clip_value[1];

          this.plot_image();

          if (persist) {
            // The parent owns the canonical msg.content arrays and builds a
            // per-image object for this index, so emit the updated request_body
            // and let the parent persist it at the right index.
            this.contentChange.emit(this.request_body);
          }
        })
        .catch((err) => {
          console.log("error caught in promise", err);
          this.notificationService.error("Error", err.message);
        });
    } catch (error) {
      console.error("Error fetching image:", error);
      this.notificationService.error("Error", error?.message);
    }
    this.loading = false;
  }

  plot_image() {
    // Remove existing image if it exists
    const existingImage = this.svg.select(".shot-image");
    if (!existingImage.empty()) {
      existingImage.remove();
    }
    // Add an image to the SVG
    this.svg
      .append("image")
      .attr("class", "shot-image")
      .attr("xlink:href", this.image_object_url)
      .on("load", () => {
        // Revoke the Object URL once the image has loaded
        // This frees up memory. Crucial for multiple image uploads.
        URL.revokeObjectURL(this.image_object_url);
      })
      .on("error", () => {
        this.notificationService.error(
          "Error",
          "Error loading image from Blob URL."
        );
        // Handle error
        this.svg
          .append("text")
          .attr("x", this.width / 2)
          .attr("y", this.height / 2)
          .attr("text-anchor", "middle")
          .attr("fill", "red")
          .text("Failed to load image.");
        URL.revokeObjectURL(this.image_object_url); // Still revoke on error
      })
      .attr("preserveAspectRatio", "none")
      .attr("width", this.width) // Desired width of the image
      .attr("height", this.height); // Desired height of the image
  }

  onScalingChange() {
    this.request_body.scaling = [this.scaling * -1, this.scaling];
  }

  async update_image_width() {
    const containerElement = this.sliceImageContainer.nativeElement;
    await new Promise((resolve) => setTimeout(resolve, 100));
    this.width = containerElement.clientWidth;
    this.svg.attr("width", this.width);
    this.svg.select("image.shot-image").attr("width", this.width);
  }

  handlePanelTypeChange(panel_type: string): void {
    if (panel_type === "Field") {
      this.request_body.panel = 1;
      this.request_body.interleave_attrs.is_interleave = false;
    } else if (panel_type === "Interleaved") {
      this.request_body.interleave_attrs.is_interleave = true;
    } else if (panel_type === "Predicted") {
      this.request_body.panel = 0;
      this.request_body.interleave_attrs.is_interleave = false;
    }
  }

  add_image_to_flipper() {
    this.imageFlipper.add_image({
      image_link: this.image_object_url,
      image_information: this.image_information,
    });
  }

  @HostListener("window:resize", ["$event"])
  sizeChange(event) {
    this.update_image_width();
  }
}
